import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../Redux/Store';
import { useAppDispatch, useAppSelector } from '../../Hooks/Hooks';
import { AppContext } from '../../Context/AppContext';
import { getContentStackData } from '../../Redux/Reducers/ContentOrConfigSlice';
import { EVENT, SCREEN } from '../../Constants/AnalyticsConstants';
import { CONTENTSTACK_PARAMS, OK_TEXT } from '../../Constants/Constants';
import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import { TSupportProps } from './Support.type';
import CustomModal from '../../Components/Shared/CustomModal/CustomModal';
import DeviceInfo from '../../Components/Shared/DeviceInfo/DeviceInfo';
import DeviceInfoTable from '../../Components/Shared/DeviceInfoTable/DeviceInfoTable';
import './Support.scss';

export const getContentFromUID = (uid: string, _contentStackData: any, cb?: () => void): any => {
  const data = [
    ...(_contentStackData?.accordion_content ?? []),
    ...(_contentStackData?.tabbed_accordion_content ?? []),
  ];
  let content: any = null;
  if (data?.length > 0) {
    if (data?.find((item: any) => item?.uid === uid)) {
      content = data?.find((item: any) => item?.uid === uid);
    } else {
      if (cb) {
        cb();
      }
    }
  }
  return content;
};

const Support = (props: TSupportProps) => {
  const { isMobile } = useContext(AppContext);
  const { contentStackData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isMobile) navigate('/more-mobile');
  }, [isMobile, navigate]);

  const [deviceInfoModalProps, setDeviceInfoModalProps] = useState({
    isModalOpen: false,
  });
  const [selectedHeader, setSelectedHeader] = useState('');
  const [openedAccordion, setOpenedAccordion] = useState<any[]>([]);
  const [subSection, setSubSection] = useState<any[]>([]);
  const [selectedSubSection, setSelectedSubSection] = useState<any>({});

  useEffect(() => {
    dispatch(
      getContentStackData({
        queryPayload: {
          contentType: 'tabbed_content,accordion_content,tabbed_accordion_content',
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const tabs = contentStackData?.tabbed_content?.find(
      (item: any) => item.uid === CONTENTSTACK_PARAMS.SUPPORT_TABS_UID,
    ).tabs;
    if (tabs?.length > 0) {
      setSelectedHeader(tabs?.[0]?.tab_title);
    }
  }, [contentStackData?.tabbed_content]);

  useEffect(() => {
    const selectedSubSection = subSection?.find((item: any) => item?.selected);
    if (selectedSubSection?._content_type_uid === 'tabbed_accordion_content') {
      const firstAccordion = selectedSubSection?.accordion_content?.[0]?.uid;
      if (!openedAccordion.includes(firstAccordion)) {
        setOpenedAccordion([firstAccordion]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subSection]);

  useEffect(() => {
    const tabs = contentStackData?.tabbed_content?.find(
      (item: any) => item.uid === CONTENTSTACK_PARAMS.SUPPORT_TABS_UID,
    ).tabs;
    let content = tabs?.find(
        (tab: any) => tab?.tab_title === selectedHeader,
      )?.content || [];
    if (content) {
      content = content.map((item: any, _idx: number) => {
        return {
          ...item,
          ...getContentFromUID(item?.uid, contentStackData),
          selected: _idx === 0,
        };
      });
      setSubSection(content);
      handleSubSectionClick(content[0]);
    }
  }, [contentStackData, selectedHeader]);

  const processEventName = (name: string) => {
    if (!name) return '';
    let processedName = name.replace(/[^a-zA-Z0-9 ]/g, ' ');
    processedName = processedName.replace(/\s+/g, '_');
    return processedName.toLowerCase();
  };
  const handleSubSectionFbEvent = (eventName: string) => {
    const processedEventName = processEventName(eventName || '');
    const processedSelectedHeader = processEventName(selectedHeader || '');
    fbLogEvent(`ue_${processedEventName}`, {
      screen: isMobile ? SCREEN.MORE_MENU : SCREEN.SUPPORT,
      lead: SCREEN.MORE_MENU,
      sectionName: processedSelectedHeader,
      subSectionName: processedEventName,
    });
  };

  const handleRowItemFbEvent = (eventName: string) => {
    const processedSubSectionName = processEventName(selectedSubSection || '');
    const processedEventName = processEventName(eventName || '');
    const processedSelectedHeader = processEventName(selectedHeader || '');
    fbLogEvent(`ue_${processedEventName}`, {
      screen: isMobile ? SCREEN.MORE_MENU : SCREEN.SUPPORT,
      lead: SCREEN.MORE_MENU,
      sectionName: processedSelectedHeader,
      subSectionName: processedSubSectionName,
      rowItemName: processedEventName,
    });
  };

  const handleSubSectionClick = (item: any) => {
    setSubSection((prev) =>
      prev.map((_item) => {
        return { ..._item, selected: _item.uid === item.uid };
      }),
    );
    const content = item?.heading || item?.tab_title || item?.title;
    setSelectedSubSection(content);
    if (content && content !== selectedSubSection) {
      handleSubSectionFbEvent(content);
    }
  };

  const handleDeviceClick = () => {
    fbLogEvent(EVENT.SV_DEVICE_INFO, {
      screen: SCREEN.DEVICE_INFO_MODAL,
      lead: SCREEN.PROFILE,
    });
    const modalProps = {
      isModalOpen: true,
      title: 'Device Info',
      titleIcon: '/oo/assets/icon/info.svg',
      additionalCssClass: 'DeviceInfo_Modal',
      size: 'large',
      children: (
        <div>
          <DeviceInfoTable />
        </div>
      ),
      buttons: [
        {
          title: OK_TEXT,
          type: 'primary-cta-btn',
          onClick: () => {
            setDeviceInfoModalProps({ isModalOpen: false });
          },
        },
      ],
    };
    setDeviceInfoModalProps(modalProps);
  };
  const handleAccordionToggle = (uid: string) => {
    setOpenedAccordion(
      openedAccordion.includes(uid)
        ? openedAccordion.filter((item) => item !== uid)
        : [...openedAccordion, uid],
    );
  };
  const renderSupportContent = () => {
    const data = subSection?.find((item: any) => item?.selected);
    if (data?._content_type_uid === 'tabbed_accordion_content') {
      return (
        <div className="Tabbed_Accordion_Content">
          {data?.accordion_content?.map((item: any) => {
            return (
              <React.Fragment key={item?.uid}>
                <div className="Accordion_Content">
                  <div className="Content_Title_Container">
                    <p className="Content_Title">
                      {getContentFromUID(item?.uid, contentStackData)?.heading}
                    </p>
                    <div
                      className="Accordion_Arrow"
                      onClick={() => {
                        handleRowItemFbEvent(
                          getContentFromUID(item?.uid, contentStackData)?.heading,
                        );
                        handleAccordionToggle(item?.uid);
                      }}
                    >
                      <img
                        src={`/oo/assets/icon/grid-${
                          openedAccordion.includes(item?.uid) ? 'up' : 'down'
                        }-arrow-thin.svg`}
                        alt="Arrow icon"
                      />
                    </div>
                  </div>
                  {openedAccordion.includes(item?.uid) && (
                    <>
                      {!!getContentFromUID(item?.uid, contentStackData)?.content ? (
                        <div
                          className="FAQ content"
                          dangerouslySetInnerHTML={{
                            __html: getContentFromUID(item?.uid, contentStackData)?.content,
                          }}
                        />
                      ) : (
                        getContentFromUID(item?.uid, contentStackData)?.faq?.map((faq: any) => {
                          return (
                            <div key={faq?._metadata?.uid}>
                              <div
                                className="FAQ question"
                                dangerouslySetInnerHTML={{
                                  __html: "<span class = 'indicator'>Q. </span>" + faq?.question,
                                }}
                              />
                              <div
                                className="FAQ answer"
                                dangerouslySetInnerHTML={{
                                  __html: "<span  class = 'indicator'>A. </span>" + faq?.answer,
                                }}
                              />
                            </div>
                          );
                        })
                      )}
                    </>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      );
    } else {
      return (
        data?.content && (
          <div
            className="Accordion_Content"
            dangerouslySetInnerHTML={{
              __html: data?.content,
            }}
          />
        )
      );
    }
  };

  return (
    <div className="Support">
      <CustomModal {...deviceInfoModalProps} />
      <DeviceInfo handleDeviceClick={handleDeviceClick} />
      <div className="Support_Header">
        {contentStackData?.tabbed_content?.find(
              (item: any) => item.uid === CONTENTSTACK_PARAMS.SUPPORT_TABS_UID,
            ).tabs?.map((tab: any) => {
          return (
            <div
              key={tab?.tab_title}
              className={`${selectedHeader === tab?.tab_title ? 'active' : ''}`}
              onClick={() => {
                setSelectedHeader(tab?.tab_title);
              }}
            >
              {tab?.tab_title}
            </div>
          );
        })}
      </div>
      {!!subSection?.length && (
        <div className="Support_SubSection">
          {subSection?.map((item: any) => (
            <div
              key={item?.uid}
              className={`${item?.selected ? 'active' : ''}`}
              onClick={() => {
                handleSubSectionClick(item);
              }}
            >
              {item?.heading || item?.tab_title || item?.title}
            </div>
          ))}
        </div>
      )}
      <div className="Support_Content_Container">{renderSupportContent()}</div>
    </div>
  );
};

export default Support;
